import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Alert,
  Tooltip,
  Paper,
  Grid,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { enGB } from 'date-fns/locale';
import {
  getCommissionPayments,
  addCommissionPayment,
  updateCommissionPayment,
  deleteCommissionPayment,
} from '../../api/commissionPayment';
import { CommissionPayment, CommissionPaymentInput } from '../../types/commissionPayment.types';

interface CommissionPaymentManagerProps {
  contractId: string;
  contractValue: number;
  onUpdate?: () => void;
}

const CommissionPaymentManager: React.FC<CommissionPaymentManagerProps> = ({
  contractId,
  contractValue,
  onUpdate,
}) => {
  const [payments, setPayments] = useState<CommissionPayment[]>([]);
  const [totalReceived, setTotalReceived] = useState(0);
  const [outstandingBalance, setOutstandingBalance] = useState(0);
  const [commissionStatus, setCommissionStatus] = useState<string>('N/A');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState<CommissionPayment | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<CommissionPayment | null>(null);

  // Form states
  const [amount, setAmount] = useState<number | undefined>(undefined);
  const [dateReceived, setDateReceived] = useState<Date | null>(new Date());
  const [notes, setNotes] = useState('');

  // History expansion state
  const [expandedHistory, setExpandedHistory] = useState<{ [key: string]: boolean }>({});

  const loadPayments = useCallback(async () => {
    try {
      setError(null);
      const data = await getCommissionPayments(contractId);
      setPayments(data.payments || []);
      setTotalReceived(data.totalCommissionReceived || 0);
      setOutstandingBalance(data.outstandingBalance || 0);
      setCommissionStatus(data.commissionStatus || 'N/A');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load commission payments');
    }
  }, [contractId]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const handleOpenDialog = (payment?: CommissionPayment) => {
    if (payment) {
      setEditingPayment(payment);
      setAmount(payment.amount);
      setDateReceived(new Date(payment.dateReceived));
      setNotes(payment.notes || '');
    } else {
      setEditingPayment(null);
      setAmount(undefined);
      setDateReceived(new Date());
      setNotes('');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPayment(null);
    setAmount(undefined);
    setDateReceived(new Date());
    setNotes('');
    setError(null);
  };

  const handleSubmit = async () => {
    console.log('handleSubmit called', { amount, dateReceived, notes });
    try {
      setError(null);
      setSuccess(null);

      if (!amount || amount <= 0) {
        console.log('Validation failed: invalid amount', amount);
        setError('Please enter a valid amount');
        return;
      }

      if (!dateReceived) {
        console.log('Validation failed: no date');
        setError('Please select a date');
        return;
      }

      const paymentData: CommissionPaymentInput = {
        amount,
        dateReceived: dateReceived.toISOString(),
        notes: notes.trim(),
      };

      console.log('Submitting payment data:', paymentData);

      if (editingPayment && editingPayment._id) {
        console.log('Updating payment:', editingPayment._id);
        await updateCommissionPayment(contractId, editingPayment._id, paymentData);
        setSuccess('Commission payment updated successfully');
      } else {
        console.log('Adding new payment to contract:', contractId);
        await addCommissionPayment(contractId, paymentData);
        setSuccess('Commission payment added successfully');
      }

      handleCloseDialog();
      await loadPayments();
      if (onUpdate) onUpdate();
    } catch (err: any) {
      console.error('Error submitting payment:', err);
      setError(err.response?.data?.message || 'Failed to save commission payment');
    }
  };

  const handleDeleteClick = (payment: CommissionPayment) => {
    setPaymentToDelete(payment);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!paymentToDelete || !paymentToDelete._id) return;

    try {
      setError(null);
      setSuccess(null);
      await deleteCommissionPayment(contractId, paymentToDelete._id);
      setSuccess('Commission payment deleted successfully');
      setDeleteConfirmOpen(false);
      setPaymentToDelete(null);
      await loadPayments();
      if (onUpdate) onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete commission payment');
    }
  };

  const toggleHistoryExpansion = (paymentId: string) => {
    setExpandedHistory((prev) => ({
      ...prev,
      [paymentId]: !prev[paymentId],
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'Partial':
        return 'warning';
      case 'Overpaid':
        return 'info';
      case 'Not Started':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(value);
  };

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Contract Value
              </Typography>
              <Typography variant="h5">{formatCurrency(contractValue)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Received
              </Typography>
              <Typography variant="h5" color="success.main">
                {formatCurrency(totalReceived)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Outstanding
              </Typography>
              <Typography
                variant="h5"
                color={outstandingBalance > 0 ? 'error.main' : outstandingBalance < 0 ? 'info.main' : 'success.main'}
              >
                {formatCurrency(outstandingBalance)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Status
              </Typography>
              <Chip label={commissionStatus} color={getStatusColor(commissionStatus)} size="medium" />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Add Payment Button */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Commission Payments</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add Payment
        </Button>
      </Box>

      {/* Payments Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date Received</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Recorded By</TableCell>
              <TableCell>Recorded At</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary">No commission payments recorded</Typography>
                </TableCell>
              </TableRow>
            ) : (
              payments.map((payment) => (
                <React.Fragment key={payment._id}>
                  <TableRow>
                    <TableCell>{format(new Date(payment.dateReceived), 'dd/MM/yyyy')}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">{formatCurrency(payment.amount)}</Typography>
                    </TableCell>
                    <TableCell>
                      {payment.recordedBy.firstName} {payment.recordedBy.lastName}
                    </TableCell>
                    <TableCell>{format(new Date(payment.recordedAt), 'dd/MM/yyyy HH:mm')}</TableCell>
                    <TableCell>{payment.notes || '-'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenDialog(payment)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(payment)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {payment.editHistory && payment.editHistory.length > 0 && (
                        <Tooltip title="View Edit History">
                          <IconButton
                            size="small"
                            onClick={() => toggleHistoryExpansion(payment._id!)}
                          >
                            {expandedHistory[payment._id!] ? <ExpandLessIcon /> : <HistoryIcon />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                  {/* Edit History Row */}
                  {payment.editHistory && payment.editHistory.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, borderBottom: 'none' }}>
                        <Collapse in={expandedHistory[payment._id!]} timeout="auto" unmountOnExit>
                          <Box sx={{ 
                            p: 2, 
                            m: 1,
                            background: 'rgba(30, 41, 59, 0.6)',
                            backdropFilter: 'blur(12px)',
                            borderRadius: 2,
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                              Edit History
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ 
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontWeight: 600,
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                                  }}>Edited At</TableCell>
                                  <TableCell sx={{ 
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontWeight: 600,
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                                  }}>Edited By</TableCell>
                                  <TableCell sx={{ 
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontWeight: 600,
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                                  }}>Previous Amount</TableCell>
                                  <TableCell sx={{ 
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontWeight: 600,
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                                  }}>Previous Date</TableCell>
                                  <TableCell sx={{ 
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontWeight: 600,
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                                  }}>Previous Notes</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {payment.editHistory.map((edit, idx) => (
                                  <TableRow key={idx} sx={{
                                    '&:hover': {
                                      backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                    }
                                  }}>
                                    <TableCell sx={{ 
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                                    }}>{format(new Date(edit.editedAt), 'dd/MM/yyyy HH:mm')}</TableCell>
                                    <TableCell sx={{ 
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                                    }}>
                                      {edit.editedBy.firstName} {edit.editedBy.lastName}
                                    </TableCell>
                                    <TableCell sx={{ 
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                                    }}>{formatCurrency(edit.previousAmount)}</TableCell>
                                    <TableCell sx={{ 
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                                    }}>{format(new Date(edit.previousDate), 'dd/MM/yyyy')}</TableCell>
                                    <TableCell sx={{ 
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                                    }}>{edit.previousNotes || '-'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 30, 30, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          {editingPayment ? 'Edit Commission Payment' : 'Add Commission Payment'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Amount (£)"
              type="number"
              value={amount || ''}
              onChange={(e) => setAmount(parseFloat(e.target.value) || undefined)}
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ mb: 2 }}
              required
            />
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
              <DatePicker
                label="Date Received"
                value={dateReceived}
                onChange={(newValue) => setDateReceived(newValue)}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    sx: { mb: 2 },
                  },
                }}
              />
            </LocalizationProvider>
            <TextField
              fullWidth
              label="Notes (Optional)"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingPayment ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 30, 30, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this commission payment of{' '}
            {paymentToDelete && formatCurrency(paymentToDelete.amount)}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommissionPaymentManager;
